import { useEffect, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import { applyShowSkeleton } from '@cartwhl/3d/gltf/utils';
import useGLTFWithCredentials from '@cartwhl/3d/r3f/hooks/useGLTFWithCredentials';
import { Character } from '@cartwhl/common/api/getCharacters';
import putCharacter from '@cartwhl/common/api/putCharacter';
import { StudioModalName } from '@cartwhl/common/stores/navigationStore';
import {
  TRACKED_FEATURES,
  trackFeatureAbandoned,
  trackFeatureCompleted,
  trackFeatureStarted,
  trackFeatureStepCompleted,
} from '@cartwhl/tracking/featureTracking';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import {
  characterPickerStore,
  navigationStore,
  noticesStore,
  sceneSettingsStore,
  webSocketStore,
} from 'stores';

import {
  BODY_BONES,
  HAND_BONES,
  MODAL_MAX_WIDTH,
  RETARGETING_STEPS,
} from '../constants';
import { CartwheelBoneKey, RetargetingBone } from '../types';
import {
  getAssignableBones,
  getInitialRetargetingBones,
  getRetargetingBones,
} from '../utils';
import AssignedIcon from './AssignedIcon';
import './CharacterRetargetingModal.css';
import LoadingCharacter from './LoadingCharacter';
import ModalLayout from './ModalLayout';
import RetargetingBonesList from './RetargetingBonesList';
import RetargetingPlayer from './RetargetingPlayer';
import Steps from './Steps';
import WaitingForServer from './WaitingForServer';

export default function CharacterRetargetingModal({
  close,
  payload,
}: {
  close: () => void;
  payload: {
    characterID: string;
    retargetingInProgress?: boolean;
    showStep?: 0 | 1 | 2;
  };
}) {
  const { characters } = useSnapshot(characterPickerStore.state);
  const character = characters?.find((c) => c.jobID === payload.characterID);

  const meshData = useGLTFWithCredentials(character?.meshPath);
  const stageRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);
  const [highlightBones, setHighlightBones] = useState<string[]>([]);

  const [step, setStep] = useState<0 | 1 | 2>(payload.showStep ?? 0);

  const [bones, setBones] = useState(getInitialRetargetingBones());

  const stepBones = bones.filter((bone) =>
    (step === 0 ? BODY_BONES : HAND_BONES).includes(bone.cartwheelBoneKey),
  );
  const assignedStepBones = useMemo(() => {
    return stepBones.filter((bone) => bone.assignedBone);
  }, [stepBones]);

  useEffect(() => {
    trackFeatureStarted(TRACKED_FEATURES.CHARACTER_UPLOAD_RETARGETING, {
      character_id: payload.characterID,
      total_steps: RETARGETING_STEPS.length,
    });
    trackFeatureStepCompleted(
      TRACKED_FEATURES.CHARACTER_UPLOAD,
      'entered_retargeting',
      {
        character_id: payload.characterID,
      },
    );
  }, []);

  useEffect(() => {
    // If the payload is changed while the modal is open, update the step
    // This is a little workaround to avoid having global state for the modal
    setStep(payload.showStep ?? 0);
  }, [payload.showStep]);

  useEffect(() => {
    // When meshData is loaded, find bones in scene and assign to retargeting bones
    if (meshData && !mountedRef.current) {
      applyShowSkeleton(meshData, false);
      onAutoAssign(bones);
      mountedRef.current = true;
    }
  }, [meshData]);

  if (character?.jobID !== payload.characterID) {
    // we need to update the character
    sceneSettingsStore.updateCharacter(payload.characterID);
  }

  const onAssign = (cartwheelBoneKey: string, boneToAssign: THREE.Bone) => {
    // If there was a bone assigned to this retargeting bone, restore its name

    const unassignedBone = bones.find(
      (bone) => bone.cartwheelBoneKey === cartwheelBoneKey,
    );
    if (unassignedBone?.assignedBone) {
      restoreBoneName(unassignedBone.assignedBone);
    }

    // Update the bone name in the scene
    setBones(
      bones.map((bone) =>
        bone.cartwheelBoneKey === cartwheelBoneKey
          ? { ...bone, assignedBone: boneToAssign }
          : bone,
      ),
    );
  };

  const onAutoAssign = (bones: RetargetingBone[]) => {
    if (!meshData?.scene) return;
    const newBones = getRetargetingBones(meshData.scene, bones);

    setBones((prevBones) => {
      const mergedBones = [...prevBones];
      newBones.forEach((newBone) => {
        const index = mergedBones.findIndex(
          (b) => b.cartwheelBoneKey === newBone.cartwheelBoneKey,
        );
        if (index >= 0) {
          mergedBones[index] = newBone;
        }
      });
      return mergedBones;
    });

    const assignedBones = newBones.filter((bone) => bone.assignedBone);
    noticesStore.success(`${assignedBones.length} bones auto-assigned`);
  };

  const onClear = () => {
    if (!meshData?.scene) return;
    let count = 0;
    setBones((prevBones) => {
      return prevBones.map((bone) => {
        if (
          bone.assignedBone &&
          stepBones.some((b) => b.cartwheelBoneKey === bone.cartwheelBoneKey)
        ) {
          count++;
          // First restore the original name
          restoreBoneName(bone.assignedBone);
          // Then return new bone object with null assignedBone
          return { ...bone, assignedBone: null };
        }
        return bone;
      });
    });
    noticesStore.info(`${count} bones unassigned`);
  };

  const onUnassign = (bone: THREE.Bone) => {
    restoreBoneName(bone);
    setBones((prevBones) => {
      return prevBones.map((b) =>
        b.assignedBone === bone ? { ...b, assignedBone: null } : b,
      );
    });
  };

  const restoreBoneName = (bone: THREE.Bone) => {
    // Reset the bone name to the original name
    bone.name = bone.userData.originalName || bone.name;
  };

  const assignableBones = useMemo(() => {
    return getAssignableBones(meshData?.scene, bones);
  }, [meshData, bones]);

  const setHighlightBone = (boneKey: string, highlight: boolean) => {
    setHighlightBones((prev) =>
      highlight ? [...prev, boneKey] : prev.filter((b) => b !== boneKey),
    );
  };

  return (
    <Modal
      title="Character Retargeting"
      open
      onOpenChange={close}
      maxWidth={MODAL_MAX_WIDTH}
      fullscreenOnMobile
      fixedHeightContent
      closeOnClickOutside={false}
    >
      <ModalLayout.Root>
        <Steps step={step} steps={RETARGETING_STEPS} />

        <div className="min-h-0 flex-1">
          <ModalLayout.Stage ref={stageRef}>
            {!payload.retargetingInProgress && (
              <ModalLayout.Sidebar>
                <Text
                  as="h2"
                  variant="studio/body/medium/md"
                  className="mb-1.5"
                >
                  {RETARGETING_STEPS[step].title}
                </Text>
                <Text
                  as="p"
                  variant="studio/body/sm"
                  className="text-secondary"
                >
                  {RETARGETING_STEPS[step].description}
                </Text>

                {step < 2 && (
                  <>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Text as="h3" variant="studio/body/medium/sm">
                          {assignedStepBones.length} of {stepBones.length}{' '}
                          assigned
                        </Text>
                        {assignedStepBones.length === stepBones.length && (
                          <AssignedIcon className="relative bottom-px text-green-50" />
                        )}
                      </div>
                      <Button
                        variant="tertiary"
                        className="text-blue dark:text-blue -my-1"
                        onClick={onClear}
                      >
                        Clear all
                      </Button>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto">
                      <RetargetingBonesList
                        stepBones={stepBones}
                        assignableBones={assignableBones}
                        onAssign={onAssign}
                        onUnassign={onUnassign}
                        stageRef={stageRef}
                        setHighlightBone={setHighlightBone}
                      />
                    </div>

                    <div className="mt-2 flex-none justify-between">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onAutoAssign(stepBones)}
                      >
                        Auto-assign
                      </Button>
                    </div>
                  </>
                )}
              </ModalLayout.Sidebar>
            )}

            <ModalLayout.Main>
              {!meshData && !payload.retargetingInProgress && (
                <LoadingCharacter />
              )}

              {meshData && !payload.retargetingInProgress && (
                <RetargetingPlayer
                  bones={bones}
                  character={character as Character}
                  highlightBones={highlightBones}
                  scene={meshData.scene}
                />
              )}
            </ModalLayout.Main>

            {payload.retargetingInProgress && (
              <WaitingForServer text="Retargeting character..." />
            )}
          </ModalLayout.Stage>
        </div>

        <ModalLayout.Buttons>
          <Button
            variant="tertiary"
            onClick={() => {
              trackFeatureAbandoned(
                TRACKED_FEATURES.CHARACTER_UPLOAD_RETARGETING,
                {
                  character_id: payload.characterID,
                  step: RETARGETING_STEPS[step].shortTitle,
                  step_index: step,
                },
              );
              trackFeatureAbandoned(TRACKED_FEATURES.CHARACTER_UPLOAD, {
                character_id: payload.characterID,
                step: 'retargeting',
              });
              close();
            }}
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                variant="secondary"
                disabled={payload.retargetingInProgress}
                onClick={() => {
                  if (step === 1) {
                    setStep(0);
                  } else if (step === 2) {
                    setStep(1);
                  }
                }}
              >
                {RETARGETING_STEPS[step].previousButtonText}
              </Button>
            )}
            {step === 0 && (
              <Button
                variant="primary"
                onClick={() => {
                  trackFeatureStepCompleted(
                    TRACKED_FEATURES.CHARACTER_UPLOAD_RETARGETING,
                    'body_bones',
                    {
                      character_id: payload.characterID,
                      step_index: 0,
                    },
                  );
                  setStep(1);
                }}
              >
                {RETARGETING_STEPS[step].nextButtonText}
              </Button>
            )}
            {step === 1 && (
              <Button
                loading={payload.retargetingInProgress}
                variant="primary"
                onClick={async () => {
                  if (!character?.jobID) return;

                  trackFeatureStepCompleted(
                    TRACKED_FEATURES.CHARACTER_UPLOAD_RETARGETING,
                    'hand_bones',
                    {
                      character_id: payload.characterID,
                      step_index: 1,
                    },
                  );

                  const predictedMapping = {} as Record<
                    CartwheelBoneKey,
                    string
                  >;
                  bones.forEach((bone) => {
                    if (bone.assignedBone?.userData.originalName) {
                      predictedMapping[bone.cartwheelBoneKey] =
                        bone.assignedBone.userData.originalName;
                    }
                  });

                  navigationStore.setModal(StudioModalName.RETARGETING, {
                    characterID: character?.jobID,
                    showStep: 2,
                    retargetingInProgress: true,
                  });

                  const { error } = await putCharacter(character?.jobID, {
                    predictedMapping,
                    wsConnectionID: webSocketStore.webSocket.connectionID,
                    wsHttpEndpoint: webSocketStore.webSocket.endpoint,
                  });

                  if (error) {
                    noticesStore.error(
                      `Something went wrong: ${error.message}`,
                      5000,
                    );
                    navigationStore.closeModal();
                  }
                }}
              >
                {RETARGETING_STEPS[step].nextButtonText}
              </Button>
            )}
            {step === 2 && (
              <Button
                variant="primary"
                onClick={() => {
                  trackFeatureCompleted(
                    TRACKED_FEATURES.CHARACTER_UPLOAD_RETARGETING,
                    { character_id: payload.characterID },
                  );
                  trackFeatureCompleted(TRACKED_FEATURES.CHARACTER_UPLOAD, {
                    character_id: payload.characterID,
                    metadata: { resolution_path: 'retargeting' },
                  });
                  close();
                }}
                disabled={payload.retargetingInProgress}
              >
                {RETARGETING_STEPS[step].nextButtonText}
              </Button>
            )}
          </div>
        </ModalLayout.Buttons>
      </ModalLayout.Root>
    </Modal>
  );
}
